import { Check, Lightbulb, Lock, Plus, Tag as TagIcon } from "lucide-react";
import { useId, useState, type FormEvent } from "react";
import { ApiError, del, post, put, type Asset, type Trip } from "../../lib/api.ts";
import { clampNote, type Tag } from "../../lib/tags.ts";
import { useApi } from "../../lib/useApi.ts";
import { ICON, RadioCard, reason, tagInk } from "../../ui/index.ts";

/**
 * Put the selected recordings under a label, or take them back out.
 *
 * One list, because there is one kind of thing to put them under. A tag is a name; a tag with a
 * note on it is what the app calls an idea, and the note is the only difference — so an idea
 * shows a lightbulb and its note underneath, a plain label shows a tag icon, and both are
 * applied exactly the same way.
 *
 * Each row leads with the folder's own colour, so a list of a dozen can be aimed at rather than
 * read down, and says where the selection already stands: on, off, or `Mixed` when some of what
 * is picked carries the tag and some does not. A tap resolves that — any tag the selection carries
 * at all comes off all of them, and a tag none of them carries goes on all of them — so the
 * selection is never left half under a label without being told.
 *
 * Creating works the same: type a name for a label, add a note and it is an idea, and say whether
 * the trip sees it. A name that already exists is not an error — it is the folder the person
 * meant — so typing an existing name files into it rather than refusing. If that folder is
 * somebody else's, the note and the colour offered along the way are not applied to it: filing
 * into a shared folder is the point, rewriting it is not.
 *
 * Rows say when a folder is private, because this is the moment of filing, and putting a
 * recording somewhere nobody else can see it should never be a surprise afterwards. Only the
 * reader's own private folders are ever here — the server does not send anyone else's.
 *
 * None of that puts the sheet away. Filing is several taps, each one answered in the row that
 * was tapped, and Done is the only thing that closes it.
 */
export function TagPanel({
  trip,
  assets,
  onDone,
  onClose,
}: {
  trip: Trip;
  /** The recordings being filed, with the tags they already carry. */
  assets: Asset[];
  /** Finished: close, and let go of the selection this was opened from. */
  onDone: () => void;
  /** Put the sheet away but keep the selection, for whoever is not finished with it. */
  onClose: () => void;
}) {
  const titleId = useId();
  const nameId = useId();
  const noteId = useId();
  const tags = useApi<{ items: Tag[] }>(`/v1/trips/${trip.id}/tags`);
  const [making, setMaking] = useState(false);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  // Shared is what a tag has always been, so it is the default, and it is sent only when it is
  // not — the server's default says it in one place rather than two.
  const [visibility, setVisibility] = useState<"PUBLIC" | "PRIVATE">("PUBLIC");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Which of the picked recordings carry each tag. Read once from what was picked and then
  // kept in step by hand: the rows have to answer "is this on?" the instant after a tap, and
  // re-reading the grid for every tap would make the sheet flicker through its own answer.
  const [carried, setCarried] = useState<Record<string, string[]>>(() => {
    const m: Record<string, string[]> = {};
    for (const a of assets) for (const t of a.tags) (m[t.id] ??= []).push(a.id);
    return m;
  });
  const [touched, setTouched] = useState(false);

  // The same filter as before, now excluding `Likes` rather than `Untagged`: it is not a tag,
  // and the heart on a recording is what fills it. Deliberately not offered here even though it
  // behaves like a private folder — this sheet files a whole selection at once, a heart is
  // public, and one tap would move everyone's counts on forty recordings.
  //
  // Another member's private folder needs no filter: the server never sends it.
  const choices = (tags.data?.items ?? []).filter((t) => t.id !== null);
  const ids = assets.map((a) => a.id);

  /**
   * Resolve a row. Anything the selection carries comes off it; a tag none of them carries goes
   * on all of them. Mixed therefore clears first and fills on the second tap, which is the only
   * order where one tap never does two different things to two halves of a selection.
   */
  async function toggle(tag: { id: string; name: string }) {
    const has = carried[tag.id] ?? [];
    const removing = has.length > 0;
    const targets = removing ? has : ids;
    setBusy(true);
    setError(null);
    let done = 0;
    let failed = 0;
    for (const assetId of targets) {
      const where = `/v1/trips/${trip.id}/assets/${assetId}/tags/${tag.id}`;
      try {
        if (removing) await del(where);
        else await put(where, {});
        done++;
      } catch (e) {
        // Already in the state that was asked for is not a failure; it is the destination.
        if (!(e instanceof ApiError && (e.status === 409 || e.status === 404))) failed++;
      }
    }
    setBusy(false);
    setTouched(true);
    if (failed > 0) {
      setError(
        `${removing ? "Removed" : "Added"} ${done} of ${targets.length} ` +
          `${removing ? "from" : "to"} “${tag.name}”. ${failed} did not go through — try again.`,
      );
      return;
    }
    // No message: the row that was tapped has just become the answer — tinted and ticked, or
    // plain again — and it is where the eye already is. A toast over the sheet would say what
    // the row says, in front of the next row somebody is reaching for. Only a tag that did not
    // go through needs words, and that is the error above.
    setCarried((c) => ({ ...c, [tag.id]: removing ? [] : [...ids] }));
  }

  async function create(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      // Creating a label that exists hands back the one that exists, so this is also how you
      // file into a label you have typed the name of rather than scrolled to.
      const made = await post<{ id: string; name: string }>(`/v1/trips/${trip.id}/tags`, {
        name: trimmed,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(visibility === "PRIVATE" ? { visibility } : {}),
      });
      setBusy(false);
      setMaking(false);
      setName("");
      setNote("");
      setVisibility("PUBLIC");
      await toggle(made);
      void tags.reload();
    } catch (err) {
      setBusy(false);
      setError(reason(err as Error));
    }
  }

  /** On, off, or neither — the three things a row can honestly say about a selection. */
  const stateOf = (tagId: string) => {
    const n = (carried[tagId] ?? []).length;
    return n === 0 ? "off" : n === assets.length ? "on" : "mixed";
  };

  return (
    // Nothing but Done puts this away. Filing takes several taps — three labels on, one off,
    // a new one made — and a sheet that leaves on a tap landing just off a row is a sheet that
    // closes itself the moment a tag is chosen. It is a bottom sheet, so everything above it is
    // backdrop: the odds of that tap are not small, and the cost is the person having to find
    // their selection again. Escape still works, because a keyboard needs a way out and a
    // keyboard does not miss.
    <div className="backdrop">
      <div
        className="tag-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key !== "Escape") return;
          if (touched) onDone();
          else onClose();
        }}
      >
        <h2 id={titleId}>
          Tag {assets.length === 1 ? "1 recording" : `${assets.length} recordings`}
        </h2>

        {/* Filing into a folder that already exists is the common act, so the list of them
            comes first; making a new one is the rarer second act and waits behind a button
            rather than pushing the list down the screen. */}
        {!making && (
          <button type="button" className="button" onClick={() => setMaking(true)}>
            <Plus {...ICON} aria-hidden="true" />
            New tag
          </button>
        )}

        {making && (
          <form className="stack" onSubmit={(e) => void create(e)} aria-label="New label">
            <label className="field">
              <span id={nameId}>Label name</span>
              <input
                aria-labelledby={nameId}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={40}
              />
            </label>
            <label className="field">
              <span id={noteId}>
                Note <span className="hint">(optional — a label with one becomes an idea)</span>
              </span>
              <input
                aria-labelledby={noteId}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={4000}
              />
            </label>
            <fieldset className="stack">
              <legend className="label">Who can see it</legend>
              <RadioCard
                name="new-label-visibility"
                value="PUBLIC"
                checked={visibility === "PUBLIC"}
                label="Everyone on the trip"
                description="They can open it and file their own recordings into it."
                onChange={() => setVisibility("PUBLIC")}
              />
              <RadioCard
                name="new-label-visibility"
                value="PRIVATE"
                checked={visibility === "PRIVATE"}
                label="Only you"
                description="Nobody else sees the folder, or what you put in it."
                onChange={() => setVisibility("PRIVATE")}
              />
            </fieldset>
            <div className="row">
              <button type="submit" className="button primary" disabled={busy || !name.trim()}>
                <Plus {...ICON} aria-hidden="true" />
                Create and tag
              </button>
              <button type="button" className="button" onClick={() => setMaking(false)}>
                Cancel
              </button>
            </div>
          </form>
        )}

        <ul className="label-list plain">
          {choices.map((t) => {
            const state = stateOf(t.id!);
            return (
              <li key={t.id}>
                <button
                  type="button"
                  className={`label-row${state === "off" ? "" : " on"}`}
                  disabled={busy}
                  aria-pressed={state === "mixed" ? "mixed" : state === "on"}
                  onClick={() => void toggle({ id: t.id!, name: t.name })}
                >
                  {/* The tag's colour on its icon, the same tint-and-ink a person's colour
                      gets on an avatar — these rows sit where people's rows do elsewhere. */}
                  <span
                    className="row-icon"
                    data-colored={t.color !== null}
                    style={tagInk(t.color)}
                    aria-hidden="true"
                  >
                    {t.note ? <Lightbulb {...ICON} /> : <TagIcon {...ICON} />}
                  </span>
                  <span className="row-main">
                    <span className="row-title">{t.name}</span>
                    <span className="row-detail">
                      {/* Said here as well as on the wall, because this is the moment of
                          filing: putting a recording somewhere nobody else can see it, or
                          somewhere the whole trip can, should never be a surprise afterwards. */}
                      {t.visibility === "PRIVATE" && (
                        <>
                          <Lock size={12} strokeWidth={2} aria-hidden="true" />
                          Only you ·{" "}
                        </>
                      )}
                      {t.note ? clampNote(t.note, 80) : `${t.count} already`}
                    </span>
                  </span>
                  {/* What this tag is to the selection, at the end of the row where the eye
                      lands after the name. Mixed is a word because there is no mark that
                      means "some of these" without being read as a half-hearted yes. */}
                  {state === "mixed" && <span className="row-mixed">Mixed</span>}
                  {state === "on" && (
                    <span className="row-on" aria-hidden="true">
                      <Check size={18} strokeWidth={3} />
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        {error && (
          <p role="alert" className="error">
            {error}
          </p>
        )}

        <button type="button" className="button primary wide" onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}
